import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaPhone, FaLinkedin, FaUser, FaChevronDown } from 'react-icons/fa6';

/**
 * MINERVA REFACTORED - Diretoria da Federação
 * Premium Design v3.0 | Chumbo + Dourado
 * Cards de Perfil | Tags de Informação | Imagens Circulares
 * Last Updated: 2026-01-29
 */

interface Director {
  id: string;
  nome: string;
  cargo: string;
  responsabilidades: string[];
  image?: string;
  email?: string;
  telefone?: string;
}

interface CommissionMember {
  nome: string;
  registro: string;
  cargo: string;
}

interface Commission {
  id: string;
  titulo: string;
  descricao: string;
  membros: CommissionMember[];
}

const Diretoria: React.FC = () => {
  const [expandedCommission, setExpandedCommission] = useState<string | null>('fiscalizacao');
  const [visibleElements, setVisibleElements] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-scroll-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const diretores: Director[] = [
    {
      id: 'presidente',
      nome: 'Jorge Pompeo Gassi',
      cargo: 'Presidente',
      responsabilidades: [
        'Representar institucionalmente a Federação',
        'Convocar e presidir reuniões e assembleias',
        'Coordenar e supervisionar planos de ação',
        'Zelar pela imagem e objetivos da entidade',
        'Assinar documentos oficiais',
      ],
      image: '/dr1.jpeg',
    },
    {
      id: 'vicepresidente',
      nome: 'Aline Gassi',
      cargo: 'Vice-Presidente',
      responsabilidades: [
        'Apoiar diretamente o Presidente',
        'Participar da definição de políticas administrativas',
        'Coordenar áreas e projetos delegados',
        'Assegurar continuidade da liderança',
      ],
      image: '/dr2.jpeg',
    },
    {
      id: 'primeiro-secretario',
      nome: 'Alexandre Gabriel Gassi',
      cargo: '1º Secretário',
      responsabilidades: [
        'Coordenar a administração da Federação',
        'Redigir e arquivar atas de reuniões',
        'Organizar deliberações e documentos oficiais',
        'Controlar registros institucionais',
        'Manter comunicação interna da diretoria',
      ],
      image: '/dr3.jpeg',
    },
    {
      id: 'segundo-secretario',
      nome: 'Sergio Roberto',
      cargo: '2º Secretário',
      responsabilidades: [
        'Auxiliar o Primeiro Secretário',
        'Apoio à gestão documental',
        'Manutenção de arquivos e correspondências',
        'Substituir o Primeiro Secretário em ausências',
        'Garantir organização e eficiência',
      ],
      image: '/dr4.jpeg',
    },
    {
      id: 'tesoureiro',
      nome: 'Rosmari Eliana Gabriel',
      cargo: 'Tesoureiro',
      responsabilidades: [
        'Administrar finanças da Federação',
        'Controlar receitas e despesas',
        'Gerir orçamentos e investimentos',
        'Apresentar relatórios regulares',
        'Garantir transparência financeira',
      ],
      image: '/dr5.jpeg',
    },
    {
      id: 'segundo-tesoureiro',
      nome: 'Saulo Alexandre',
      cargo: '2º Tesoureiro',
      responsabilidades: [
        'Auxiliar o Tesoureiro nas atividades financeiras',
        'Controle de movimentações e registros',
        'Apoio à prestação de contas',
        'Substituir Tesoureiro em ausências',
        'Assegurar integridade financeira',
      ],
      image: '/dr6.jpeg',
    },
  ];

  const comissoes: Commission[] = [
    {
      id: 'fiscalizacao',
      titulo: 'Comissão de Fiscalização',
      descricao:
        'Responsável por zelar pela integridade, legalidade e conformidade das atividades exercidas pelos profissionais, escolas e entidades filiadas à Federação. Atua com independência e rigor, garantindo que todos sigam os padrões éticos, técnicos e regulamentares.',
      membros: [
        { nome: 'Erica Macedo', registro: 'FEPCSSP-144280', cargo: 'Presidente da Comissão' },
        { nome: 'Edson França', registro: 'FEPCSSP-100989', cargo: 'Membro' },
        { nome: 'Fernando Monteiro', registro: 'FEPCSSP-142930', cargo: 'Membro' },
        { nome: 'Clemilson Pinheiro', registro: 'FEPCSSP-121703', cargo: 'Membro' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        [data-scroll-animate] {
          opacity: 0;
          animation: fadeInUp 0.7s ease-out forwards;
        }

        .scroll-visible {
          opacity: 1 !important;
        }

        [data-delay="1"] { animation-delay: 0.1s; }
        [data-delay="2"] { animation-delay: 0.2s; }
        [data-delay="3"] { animation-delay: 0.3s; }
        [data-delay="4"] { animation-delay: 0.4s; }
        [data-delay="5"] { animation-delay: 0.5s; }
        [data-delay="6"] { animation-delay: 0.6s; }

        .card-shadow {
          box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.15);
        }

        .card-shadow:hover {
          box-shadow: 0 20px 60px -10px rgba(0, 0, 0, 0.2);
        }

        .gold-gradient {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        }

        .director-card {
          position: relative;
          overflow: hidden;
        }

        .director-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #f59e0b 0%, #d97706 50%, #f59e0b 100%);
          background-size: 200% 100%;
          animation: shimmer 3s linear infinite;
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .image-container {
          position: relative;
          width: 180px;
          height: 180px;
          flex-shrink: 0;
        }

        .image-container::before {
          content: '';
          position: absolute;
          inset: -3px;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          border-radius: 50%;
          z-index: 0;
        }

        .image-container img {
          position: relative;
          z-index: 1;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid white;
        }

        @media (max-width: 768px) {
          .image-container {
            width: 140px;
            height: 140px;
          }
        }
      `}</style>

      {/* HERO SECTION */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-amber-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber-600 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div 
            id="hero-title"
            data-scroll-animate
            className="max-w-4xl mx-auto text-center scroll-visible"
          >
            <div className="inline-block px-4 py-2 bg-amber-600/20 border border-amber-500/40 rounded-full mb-6">
              <span className="text-amber-400 text-sm font-black uppercase tracking-wide">
                Liderança e Transparência
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-6 bg-gradient-to-r from-white via-amber-100 to-white bg-clip-text text-transparent">
              Nossa Diretoria
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto">
              Profissionais dedicados que conduzem a Federação com ética, transparência e compromisso
              com a excelência do setor da beleza
            </p>
          </div>
        </div>
      </section>

      {/* DIRETORES */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-white to-gray-50">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-6xl mx-auto">
            <h2 
              id="directors-title"
              data-scroll-animate
              className="text-3xl md:text-4xl font-black text-gray-900 mb-4 text-center scroll-visible"
            >
              Membros da Diretoria
            </h2>
            <p 
              id="directors-subtitle"
              data-scroll-animate
              data-delay="1"
              className="text-center text-gray-600 mb-16 max-w-2xl mx-auto scroll-visible"
            >
              Conheça os líderes que dedicam seu tempo e expertise para fortalecer nossa Federação
            </p>

            <div className="space-y-8">
              {diretores.map((diretor, idx) => (
                <div
                  key={diretor.id}
                  id={`director-${diretor.id}`}
                  data-scroll-animate
                  data-delay={String(Math.min(idx + 2, 6))}
                  className="director-card bg-white rounded-2xl p-6 md:p-8 card-shadow transition-all hover:scale-[1.01] scroll-visible"
                >
                  <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                    {/* Imagem do Diretor */}
                    {diretor.image && (
                      <div className="flex justify-center md:justify-start">
                        <div className="image-container">
                          <img
                            src={diretor.image}
                            alt={diretor.nome}
                            className="shadow-xl"
                          />
                        </div>
                      </div>
                    )}

                    {/* Conteúdo do Card */}
                    <div className="flex-1 space-y-5">
                      {/* Header */}
                      <div className="relative">
                        <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-2">
                          {diretor.nome}
                        </h3>
                        <div className="inline-block px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg shadow-md">
                          <span className="text-white text-sm font-black uppercase tracking-wide">
                            {diretor.cargo}
                          </span>
                        </div>
                      </div>

                      {/* Responsabilidades */}
                      <div>
                        <h4 className="font-black text-gray-900 mb-3 text-sm uppercase tracking-wide">
                          Responsabilidades
                        </h4>
                        <ul className="space-y-2">
                          {diretor.responsabilidades.map((resp, respIdx) => (
                            <li key={respIdx} className="flex items-start gap-3">
                              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex-shrink-0 mt-1.5" />
                              <span className="text-gray-700 text-sm leading-relaxed">{resp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Contact Tags */}
                      <div className="space-y-3 relative z-10 pt-4 border-t border-gray-200">
                        {diretor.email && (
                          <a
                            href={`mailto:${diretor.email}`}
                            className="flex items-center gap-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200/60 hover:border-amber-400 transition-all group"
                          >
                            <FaEnvelope className="w-4 h-4 text-amber-600 group-hover:text-amber-700" />
                            <span className="text-sm font-medium text-gray-700 group-hover:text-amber-600 break-all">
                              {diretor.email}
                            </span>
                          </a>
                        )}
                        {diretor.telefone && (
                          <a
                            href={`tel:${diretor.telefone}`}
                            className="flex items-center gap-3 p-3 bg-gradient-to-r from-gray-100 to-gray-50 rounded-lg border border-gray-200 hover:border-gray-400 transition-all group"
                          >
                            <FaPhone className="w-4 h-4 text-gray-600 group-hover:text-gray-700" />
                            <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                              {diretor.telefone}
                            </span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMISSÕES */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 
              id="commission-title"
              data-scroll-animate
              className="text-3xl md:text-4xl font-black text-gray-900 mb-4 text-center scroll-visible"
            >
              Órgãos de Fiscalização
            </h2>
            <p 
              id="commission-desc"
              data-scroll-animate
              data-delay="1"
              className="text-center text-gray-600 mb-12 max-w-2xl mx-auto scroll-visible"
            >
              Responsáveis por garantir ética, integridade e conformidade em todas as atividades da Federação
            </p>

            <div className="space-y-4">
              {comissoes.map((comissao, idx) => (
                <div 
                  key={comissao.id}
                  id={`commission-${comissao.id}`}
                  data-scroll-animate
                  data-delay={String(Math.min(idx + 2, 6))}
                  className="bg-white rounded-2xl overflow-hidden border-2 border-amber-200/60 shadow-lg scroll-visible"
                >
                  {/* Header */}
                  <button
                    onClick={() => setExpandedCommission(expandedCommission === comissao.id ? null : comissao.id)}
                    className="commission-header w-full bg-gradient-to-r from-gray-800 to-gray-900 p-6 md:p-8 text-white flex items-center justify-between hover:opacity-95 transition-all relative"
                  >
                    <div className="text-left relative z-10">
                      <h3 className="text-xl md:text-2xl font-black">{comissao.titulo}</h3>
                      <p className="text-amber-200 text-sm mt-2 font-medium">
                        {comissao.membros.length} membros
                      </p>
                    </div>
                    <FaChevronDown
                      className={`w-6 h-6 transition-transform flex-shrink-0 relative z-10 ${
                        expandedCommission === comissao.id ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Content */}
                  {expandedCommission === comissao.id && (
                    <div className="p-6 md:p-8 bg-gradient-to-br from-gray-50 to-white">
                      {/* Descrição */}
                      <p className="text-gray-700 leading-relaxed mb-8 text-lg">
                        {comissao.descricao}
                      </p>

                      {/* Atuação */}
                      <div className="mb-8">
                        <h4 className="font-black text-gray-900 mb-4 text-sm uppercase tracking-wide">
                          Sua atuação inclui:
                        </h4>
                        <ul className="space-y-3">
                          {[
                            'Acompanhamento das ações dos Filiados e instituições credenciadas',
                            'Análise de condutas e denúncias',
                            'Emissão de pareceres sobre possíveis infrações ou desvios',
                            'Reforço da credibilidade e excelência profissional no setor',
                          ].map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start gap-3">
                              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                                <span className="text-white text-xs font-black">✓</span>
                              </div>
                              <span className="text-gray-700 leading-relaxed">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Membros */}
                      <div>
                        <h4 className="font-black text-gray-900 mb-4 text-sm uppercase tracking-wide">
                          Membros da Comissão
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {comissao.membros.map((membro, membroIdx) => (
                            <div
                              key={membroIdx}
                              className="member-item p-4 bg-white rounded-lg border-2 border-amber-200/40 hover:border-amber-300 transition-colors"
                              style={{
                                animation: `fadeInUp 0.5s ease-out ${membroIdx * 50}ms both`,
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                                  <FaUser className="w-5 h-5 text-amber-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h5 className="font-black text-gray-900 text-sm">{membro.nome}</h5>
                                  <p className="text-xs text-amber-600 font-semibold">{membro.cargo}</p>
                                  <p className="text-xs text-gray-500 mt-1">{membro.registro}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Nota */}
                      <div className="mt-8 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200/60">
                        <p className="text-sm text-gray-700">
                          <span className="font-black text-amber-600">Nota: </span>
                          A Comissão é composta por membros experientes, imparciais e comprometidos com a
                          ética e o fortalecimento da profissão.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-6 md:px-12">
          <div 
            id="cta-final"
            data-scroll-animate
            className="max-w-3xl mx-auto text-center scroll-visible"
          >
            <h2 className="text-3xl md:text-4xl font-black mb-6">
              Liderança com Propósito
            </h2>
            <p className="text-lg text-gray-100 mb-8 leading-relaxed">
              Nossa Diretoria trabalha incansavelmente para fortalecer a Federação, proteger os interesses
              dos profissionais e elevar o padrão de excelência no setor da beleza.
            </p>
            <a
              href="#"
              className="inline-block px-8 py-4 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-black rounded-lg hover:shadow-lg transition-all"
            >
              Entre em Contato com a Diretoria
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Diretoria;